import type { Breakpoint, CSSObject, TypographyVariantsOptions } from '@mui/material/styles';
import { createTheme as getTheme } from '@mui/material/styles';

import { pxToRem, setFont } from 'minimal-shared/utils';

import { themeConfig } from '../theme-config';

/**
 * TypeScript (type definition and extension)
 * @to {@link file://./../extend-theme-types.d.ts}
 */
export type FontStyleExtend = {
    fontWeightSemiBold: CSSObject['fontWeight'];
    fontSecondaryFamily: CSSObject['fontFamily'];
};

export type ResponsiveFontSizesInput = Partial<Record<Breakpoint, number>>;
export type ResponsiveFontSizesResult = Record<string, { fontSize: string }>;

const defaultMuiTheme = getTheme();

/**
 * Generates responsive font sizes based on different breakpoints.
 *
 * This function takes an object with breakpoint-specific font sizes and
 * transforms them into a format compatible with MUI's theme system,
 * converting pixel values to rem units for each specified breakpoint.
 *
 * @param obj - An object containing font sizes (in px) for different breakpoints
 * @returns An object with media queries as keys and font size styles as values
 *
 * @example
 * ```typescript
 * const fontSizes = responsiveFontSizes({
 *   xs: 14,
 *   md: 16,
 *   lg: 18
 * });
 * // Result:
 * // {
 * //   '@media (min-width:0px)': { fontSize: '0.875rem' },
 * //   '@media (min-width:900px)': { fontSize: '1rem' },
 * //   '@media (min-width:1200px)': { fontSize: '1.125rem' }
 * // }
 * ```
 */
function responsiveFontSizes(obj: ResponsiveFontSizesInput): ResponsiveFontSizesResult {
    const breakpoints: Breakpoint[] = defaultMuiTheme.breakpoints.keys;

    return breakpoints.reduce((acc, breakpoint) => {
        const value = obj[breakpoint];

        if (value !== undefined && value >= 0) {
            acc[defaultMuiTheme.breakpoints.up(breakpoint)] = {
                fontSize: pxToRem(value),
            };
        }

        return acc;
    }, {} as ResponsiveFontSizesResult);
}

export const defaultFont = 'Public Sans';

const primaryFont = setFont(themeConfig.fontFamily.primary);
const secondaryFont = setFont(themeConfig.fontFamily.secondary);

export const typography: TypographyVariantsOptions = {
    fontFamily: primaryFont,
    fontSecondaryFamily: secondaryFont,
    fontWeightLight: '300',
    fontWeightRegular: '400',
    fontWeightMedium: '500',
    fontWeightSemiBold: '600',
    fontWeightBold: '700',
    h1: {
        fontFamily: secondaryFont,
        fontWeight: 800,
        lineHeight: 80 / 64,
        fontSize: pxToRem(40),
        ...responsiveFontSizes({ sm: 52, md: 58, lg: 64 }),
    },
    h2: {
        fontFamily: secondaryFont,
        fontWeight: 800,
        lineHeight: 64 / 48,
        fontSize: pxToRem(32),
        ...responsiveFontSizes({ sm: 40, md: 44, lg: 48 }),
    },
    h3: {
        fontFamily: secondaryFont,
        fontWeight: 700,
        lineHeight: 1.5,
        fontSize: pxToRem(24),
        ...responsiveFontSizes({ sm: 26, md: 30, lg: 32 }),
    },
    h4: {
        fontWeight: 700,
        lineHeight: 1.5,
        fontSize: pxToRem(20),
        ...responsiveFontSizes({ md: 24 }),
    },
    h5: {
        fontWeight: 700,
        lineHeight: 1.5,
        fontSize: pxToRem(18),
        ...responsiveFontSizes({ sm: 19 }),
    },
    h6: {
        fontWeight: 600,
        lineHeight: 28 / 18,
        fontSize: pxToRem(17),
        ...responsiveFontSizes({ sm: 18 }),
    },
    subtitle1: {
        fontWeight: 600,
        lineHeight: 1.5,
        fontSize: pxToRem(16),
    },
    subtitle2: {
        fontWeight: 600,
        lineHeight: 22 / 14,
        fontSize: pxToRem(14),
    },
    body1: {
        lineHeight: 1.5,
        fontSize: pxToRem(16),
    },
    body2: {
        lineHeight: 22 / 14,
        fontSize: pxToRem(14),
    },
    caption: {
        lineHeight: 1.5,
        fontSize: pxToRem(12),
    },
    overline: {
        fontWeight: 700,
        lineHeight: 1.5,
        fontSize: pxToRem(12),
        textTransform: 'uppercase',
    },
    button: {
        fontWeight: 700,
        lineHeight: 24 / 14,
        fontSize: pxToRem(14),
        textTransform: 'unset',
    },
};
